/** Pure helpers for Color Picker from Image: palette, CMYK, loupe geometry and click-to-pixel mapping. All on-device. */

export const rgbToHex = (r: number, g: number, b: number): string => "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");

/** Simple (profile-free) RGB to CMYK, whole percentages. Print profiles differ, so treat it as a starting point. */
export function rgbToCmyk(r: number, g: number, b: number): { c: number; m: number; y: number; k: number } {
  const rp = r / 255, gp = g / 255, bp = b / 255;
  const k = 1 - Math.max(rp, gp, bp);
  if (k >= 1) return { c: 0, m: 0, y: 0, k: 100 };
  const f = (v: number) => Math.round(((1 - v - k) / (1 - k)) * 100);
  return { c: f(rp), m: f(gp), y: f(bp), k: Math.round(k * 100) };
}
export const cmykString = (r: number, g: number, b: number): string => {
  const { c, m, y, k } = rgbToCmyk(r, g, b);
  return `cmyk(${c}%, ${m}%, ${y}%, ${k}%)`;
};

export interface PaletteColor { hex: string; rgb: [number, number, number]; share: number }

/**
 * The most common colours in an image: pixels are grouped into 4-bit-per-channel buckets, each bucket is represented by the
 * average of its own pixels (so a flat #ff0000 area stays exactly #ff0000), and near-identical buckets are folded into the larger one.
 */
export function dominantColors(data: Uint8ClampedArray, width: number, height: number, max = 8, minDistance = 40): PaletteColor[] {
  const step = Math.max(1, Math.floor(Math.sqrt((width * height) / 60000)));
  const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
  let total = 0;
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 128) continue;
      const key = ((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4);
      const e = buckets.get(key);
      if (e) { e.n++; e.r += data[i]; e.g += data[i + 1]; e.b += data[i + 2]; }
      else buckets.set(key, { n: 1, r: data[i], g: data[i + 1], b: data[i + 2] });
      total++;
    }
  }
  if (!total) return [];
  const sorted = Array.from(buckets.values()).sort((a, b) => b.n - a.n);
  const picked: { rgb: [number, number, number]; n: number }[] = [];
  const dist = (a: number[], b: number[]) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  for (const e of sorted) {
    const rgb: [number, number, number] = [Math.round(e.r / e.n), Math.round(e.g / e.n), Math.round(e.b / e.n)];
    const near = picked.find((p) => dist(p.rgb, rgb) < minDistance);
    if (near) { near.n += e.n; continue; }
    if (picked.length < max) picked.push({ rgb, n: e.n });
  }
  return picked
    .sort((a, b) => b.n - a.n)
    .map((p) => ({ hex: rgbToHex(...p.rgb), rgb: p.rgb, share: p.n / total }))
    .filter((p, i) => i === 0 || p.share >= 0.005);
}

/**
 * Where a click lands on the image when the canvas is shown with object-fit: contain: the picture is centred inside the element
 * box with empty bars, so the box size alone gives the wrong pixel. Returns null for a click on a bar.
 */
export function clientToPixel(
  box: { left: number; top: number; width: number; height: number },
  imgW: number, imgH: number, clientX: number, clientY: number,
): { x: number; y: number } | null {
  if (box.width <= 0 || box.height <= 0 || imgW <= 0 || imgH <= 0) return null;
  const k = Math.min(box.width / imgW, box.height / imgH);
  const cw = imgW * k, ch = imgH * k;
  const ox = (box.width - cw) / 2, oy = (box.height - ch) / 2;
  const px = (clientX - box.left - ox) / k, py = (clientY - box.top - oy) / k;
  if (px < 0 || py < 0 || px >= imgW || py >= imgH) return null;
  return { x: Math.floor(px), y: Math.floor(py) };
}

/** Source and destination rectangles for a loupe showing (2*radius+1) pixels square around (x, y), clipped at the image edge. */
export function loupeRects(x: number, y: number, radius: number, imgW: number, imgH: number, zoom: number) {
  const size = radius * 2 + 1;
  const sx0 = x - radius, sy0 = y - radius;
  const sx = Math.max(0, sx0), sy = Math.max(0, sy0);
  const ex = Math.min(imgW, sx0 + size), ey = Math.min(imgH, sy0 + size);
  return { sx, sy, sw: Math.max(0, ex - sx), sh: Math.max(0, ey - sy), dx: (sx - sx0) * zoom, dy: (sy - sy0) * zoom, dw: Math.max(0, ex - sx) * zoom, dh: Math.max(0, ey - sy) * zoom, size: size * zoom };
}

export const LOUPE_RADIUS = 7; // 15 x 15 pixels
export const LOUPE_ZOOM = 8;   // each image pixel is drawn 8 x 8, so pixel boundaries are visible
